import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_LINKS = [
  { label: 'HOME',               href: '#home',    section: 'home' },
  { label: 'MUSIC',              href: '#music',   section: 'music' },
  { label: 'EVENTS',             href: '#events',  section: 'events' },
  { label: 'ABOUT',              href: '#about',   section: 'about' },
  { label: 'BRAND COLLABORATION', href: '#contact', section: 'brand' },
  { label: 'CONTACT',            href: '#contact', section: 'contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const [active, setActive]     = useState('home');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll-spy: watch each section and update active nav item
  useEffect(() => {
    const ids = [...new Set(NAV_LINKS.map(l => l.section))];
    const observers = [];
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { threshold: 0.25 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <motion.nav
        className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Desktop links */}
        <ul className="navbar__links">
          {NAV_LINKS.map(({ label, href, section }) => (
            <li key={label}>
              <a
                href={href}
                className={`navbar__link${active === section ? ' navbar__link--active' : ''}`}
                onClick={() => setActive(section)}
              >
                {label}
                {active === section && (
                  <motion.span
                    className="navbar__link-bar"
                    layoutId="nav-bar"
                    transition={{ type: 'spring', stiffness: 500, damping: 38 }}
                  />
                )}
              </a>
            </li>
          ))}
        </ul>

        {/* Hamburger — mobile only */}
        <button
          className="navbar__hamburger"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          <span className={`navbar__ham-line${open ? ' navbar__ham-line--open' : ''}`} />
          <span className={`navbar__ham-line${open ? ' navbar__ham-line--open' : ''}`} />
          <span className={`navbar__ham-line${open ? ' navbar__ham-line--open' : ''}`} />
        </button>

        <a href="#contact" className="navbar__book">Book Now</a>
      </motion.nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={close}
            />
            <motion.div
              className="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            >
              <ul className="mobile-menu__links">
                {NAV_LINKS.map(({ label, href }, i) => (
                  <motion.li
                    key={label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <a href={href} className="mobile-menu__link" onClick={close}>{label}</a>
                  </motion.li>
                ))}
              </ul>
              <a href="#contact" className="mobile-menu__book" onClick={close}>Book Now</a>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
